import React, { useState, useEffect, useCallback } from "react";
import { View, Platform } from "react-native";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import { router } from "expo-router";
import { trackOnb } from "../analytics/trackOnboarding";
import { useOnboardingStore } from "../store/onboardingStore";
import { fonts, theme } from "@theme";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedReaction,
  runOnJS,
  clamp,
  type SharedValue,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import * as Haptics from "expo-haptics";
import { captureException } from "@utils/captureException";
import IntroLayout from "./IntroLayout";
import PeopleStatPill from "./PeopleStatPill";

const ACCENT = "#3A83F7";
const MIN_YEARS = 0;
const MAX_YEARS = 5;
const DEFAULT_YEARS = 2;

const THUMB_SIZE = 20;
const TRACK_HEIGHT = 6;

const PEOPLE_STATS_BY_TYPE: Record<string, string[]> = {
  casual: ["+14M", "+11M", "+8.5M", "+5.8M", "+3.4M", "+1.9M"],
  "music-lover": ["+12M", "+9.2M", "+6.8M", "+4.5M", "+2.7M", "+1.4M"],
  superfan: ["+8.5M", "+6.1M", "+4.2M", "+2.6M", "+1.5M", "+0.8M"],
};

const getLabel = (value: number) => (value >= MAX_YEARS ? "5+" : `${value}`);
const getSubtitle = (value: number) =>
  value === 0
    ? "Just started"
    : value >= MAX_YEARS
      ? "Years or more"
      : value === 1
        ? "Year"
        : "Years";

// --- Stepped Slider (fully on UI thread) ---

const SteppedSlider = ({
  value,
  min,
  max,
  accentColor,
  trackColor,
}: {
  value: SharedValue<number>;
  min: number;
  max: number;
  accentColor: string;
  trackColor: string;
}) => {
  const trackWidth = useSharedValue(0);

  const gesture = Gesture.Pan()
    .minDistance(0)
    .onStart((e) => {
      "worklet";
      if (trackWidth.value === 0) return;
      const fraction = clamp(e.x / trackWidth.value, 0, 1);
      value.set(Math.round(fraction * (max - min) + min));
    })
    .onUpdate((e) => {
      "worklet";
      if (trackWidth.value === 0) return;
      const fraction = clamp(e.x / trackWidth.value, 0, 1);
      value.set(Math.round(fraction * (max - min) + min));
    });

  const fillStyle = useAnimatedStyle(() => {
    const fraction = (value.value - min) / (max - min);
    return { width: fraction * trackWidth.value };
  });

  const thumbStyle = useAnimatedStyle(() => {
    const fraction = (value.value - min) / (max - min);
    return {
      transform: [{ translateX: fraction * trackWidth.value - THUMB_SIZE / 2 }],
    };
  });

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        onLayout={(e) => {
          trackWidth.value = e.nativeEvent.layout.width;
        }}
        style={{
          height: THUMB_SIZE + 20,
          justifyContent: "center",
        }}
      >
        <View
          style={{
            height: TRACK_HEIGHT,
            borderRadius: TRACK_HEIGHT / 2,
            backgroundColor: trackColor,
          }}
        >
          <Animated.View
            style={[
              {
                height: TRACK_HEIGHT,
                borderRadius: TRACK_HEIGHT / 2,
                backgroundColor: accentColor,
              },
              fillStyle,
            ]}
          />
        </View>
        <Animated.View
          style={[
            {
              position: "absolute",
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              borderRadius: THUMB_SIZE / 2,
              backgroundColor: accentColor,
            },
            thumbStyle,
          ]}
        />
      </Animated.View>
    </GestureDetector>
  );
};

// --- Animated Number Display (style-based, no remount) ---

const NumberDisplay = ({ value }: { value: number }) => {
  const label = getLabel(value);
  const subtitle = getSubtitle(value);

  return (
    <LBox alignItems="center" style={{ minHeight: 100 }}>
      <LBox
        style={{ height: 72, alignItems: "center", justifyContent: "center" }}
      >
        <LText
          fontSize={64}
          fontFamily={fonts.bold}
          color="white"
          lineHeight={72}
          textAlign="center"
        >
          {label}
        </LText>
      </LBox>
      <LText fontSize={16} fontFamily={fonts.medium} color="whiteTranslucent60">
        {subtitle}
      </LText>
    </LBox>
  );
};

// --- Quiz2 Screen ---

const Quiz2Screen = () => {
  const sliderValue = useSharedValue(DEFAULT_YEARS);
  const [years, setYears] = useState(DEFAULT_YEARS);
  const listenerType = useOnboardingStore((s) => s.quizListenerType);
  const setQuizYearsStreaming = useOnboardingStore(
    (s) => s.setQuizYearsStreaming,
  );

  useEffect(() => {
    trackOnb("signup", "quiz-2", "view");
  }, []);

  const fireHaptic = useCallback(() => {
    if (Platform.OS !== "web") {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch((error) => {
        captureException(error, {
          channel: "ignore",
          area: "haptics",
          operation: "quiz-2-slider-impact",
        });
      });
    }
  }, []);

  // Sync shared value step changes → React state + haptics
  useAnimatedReaction(
    () => Math.round(sliderValue.value),
    (current, previous) => {
      if (previous !== null && current !== previous) {
        runOnJS(setYears)(current);
        runOnJS(fireHaptic)();
      }
    },
  );

  const handleMinus = useCallback(() => {
    sliderValue.set(Math.max(MIN_YEARS, Math.round(sliderValue.value) - 1));
  }, [sliderValue]);

  const handlePlus = useCallback(() => {
    sliderValue.set(Math.min(MAX_YEARS, Math.round(sliderValue.value) + 1));
  }, [sliderValue]);

  const handleContinue = () => {
    const finalYears = Math.round(sliderValue.value);
    setQuizYearsStreaming(finalYears);
    trackOnb("signup", "quiz-2", "answer-selected", { answer: finalYears });
    router.navigate("/onboarding/analysing");
  };

  const stats = PEOPLE_STATS_BY_TYPE[listenerType ?? "music-lover"];
  const peopleStat = `${stats[years]} people in common`;

  return (
    <IntroLayout
      totalSteps={2}
      currentStep={2}
      buttonLabel="Continue"
      buttonEventName="quiz2_continue"
      onButtonPress={handleContinue}
    >
      <LText
        fontSize={32}
        fontFamily={fonts.bold}
        lineHeight={38}
        textAlign="center"
        mb="s3"
      >
        How long have you been streaming for?
      </LText>

      <LBox alignItems="center" mb="l2" style={{ minHeight: 36 }}>
        <PeopleStatPill text={peopleStat} />
      </LBox>

      <NumberDisplay value={years} />

      <LBox flexDirection="row" alignItems="center" gap="s3" mt="l1" px="s1">
        <LPressable
          fadeEnabled
          sentryEventName="quiz2_minus"
          onPress={handleMinus}
          style={{
            width: 48,
            height: 48,
            borderRadius: 16,
            backgroundColor: theme.colors.closeButtonBg,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LText
            fontSize={28}
            fontFamily={fonts.medium}
            color="white"
            lineHeight={32}
          >
            −
          </LText>
        </LPressable>

        <LBox flex={1}>
          <SteppedSlider
            value={sliderValue}
            min={MIN_YEARS}
            max={MAX_YEARS}
            accentColor={ACCENT}
            trackColor={theme.colors.greyBorder}
          />
        </LBox>

        <LPressable
          fadeEnabled
          sentryEventName="quiz2_plus"
          onPress={handlePlus}
          style={{
            width: 48,
            height: 48,
            borderRadius: 16,
            backgroundColor: theme.colors.closeButtonBg,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LText
            fontSize={28}
            fontFamily={fonts.medium}
            color="white"
            lineHeight={32}
          >
            +
          </LText>
        </LPressable>
      </LBox>
    </IntroLayout>
  );
};

export default Quiz2Screen;
